// PARITY-0: the board settings sheet's "General" tab (order 0): what the board is — its name,
// its colour (the board's mark, or "By name" when no colour is picked), its statuses and whether
// it is archived. The board row has no description field yet, so none is shown. The name and
// colour edits live where they already are (the header's colour button, the "…" menu).
// wp/ACTIONS: whoever may edit the board edits its statuses here (the board's columns): add one,
// rename, move up / down and remove as hover icons on each row, each one `PATCH boards/{id}`
// with the whole `status_set`. The server holds the rules (a removed status may not still hold
// tasks; at least one status stays done) and its refusal shows under the list as it reads.
import {
  applyGatePreset,
  CONTENT_PIPELINE_PRESETS,
  matchingGatePreset,
} from "@companyos/domain-work/boards";
import { Badge, BOARD_COLORS, Button, cn, Input } from "@companyos/ui";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CheckIcon,
  LockIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
} from "lucide-react";
import { type FormEvent, useState } from "react";
import { problemText } from "../../settings/problem-text";
import { BoardColorButton } from "../board-controls";
import type { Board } from "../view-engine";
import { OwnerPicker } from "./owner-picker";
import { type BoardSettingsTabProps, registerBoardSettingsTab } from "./registry";

type Status = Board["status_set"][number];

/** A status key from its name (`^[a-z][a-z0-9_]*$`), unique among `taken`. */
export function statusKey(name: string, taken: ReadonlySet<string>): string {
  const base =
    name
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^[^a-z]+|_+$/g, "")
      .slice(0, 40) || "status";
  let key = base;
  for (let n = 2; taken.has(key); n++) key = `${base}_${n}`;
  return key;
}

export function GeneralTab({ board, can, saveStatuses, api, co }: BoardSettingsTabProps) {
  const colour = BOARD_COLORS.find((c) => c.color === board.color)?.label ?? "By name";
  const editable = Boolean(saveStatuses) && can.edit && !board.archived_at;
  return (
    <dl
      data-tab="general"
      className="m-0 grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-3 text-sm"
    >
      <dt className="text-muted-foreground">Name</dt>
      <dd className="m-0 min-w-0 truncate font-medium text-foreground">{board.name}</dd>
      <dt className="text-muted-foreground">Colour</dt>
      <dd className="m-0 flex items-center gap-2">
        <BoardColorButton board={board} size="xs" />
        {colour}
      </dd>
      {/* T6-BOARD-NOTE: who runs the board (a DE, or a person); also on "How it works" */}
      {api && co ? (
        <>
          <dt className="text-muted-foreground">Owner</dt>
          <dd className="m-0 min-w-0">
            <OwnerPicker
              api={api}
              co={co}
              board={board}
              editable={can.edit && !board.archived_at}
            />
          </dd>
        </>
      ) : null}
      <dt className="self-start text-muted-foreground">Statuses</dt>
      {editable && saveStatuses ? (
        <dd className="m-0 min-w-0">
          <StatusEditor statuses={board.status_set} save={saveStatuses} />
        </dd>
      ) : (
        <dd className="m-0 flex flex-wrap gap-1">
          {board.status_set.map((s) => (
            <Badge key={s.key} variant="neutral">
              {s.gate ? <ColumnLock /> : null}
              {s.name}
            </Badge>
          ))}
        </dd>
      )}
      {/* BOARD-GATES §2.5: who reviews, as one choice (content pipeline boards) */}
      {presetsFit(board.status_set) ? (
        <>
          <dt className="self-start text-muted-foreground">Who reviews</dt>
          <dd className="m-0 min-w-0">
            <GatePresetPicker
              board={board}
              editable={editable}
              save={saveStatuses}
              putRow={
                api && co
                  ? (body) => api.client.boards.putGate({ params: { co, id: board.id }, body })
                  : undefined
              }
            />
          </dd>
        </>
      ) : null}
      {board.archived_at ? (
        <>
          <dt className="text-muted-foreground">Archived</dt>
          <dd className="m-0">{new Date(board.archived_at).toLocaleDateString()}</dd>
        </>
      ) : null}
    </dl>
  );
}

/** BOARD-GATES: the mark of a column only a person moves cards out of. */
function ColumnLock() {
  return (
    <LockIcon
      role="img"
      aria-label="Only a person moves cards out"
      className="size-3 shrink-0 text-muted-foreground"
    >
      <title>Only a person moves cards out (Approve, Rework or Reject)</title>
    </LockIcon>
  );
}

/** Whether the board has every column the presets name (a content pipeline board). */
export function presetsFit(set: readonly Status[]): boolean {
  const keys = new Set(set.map((s) => s.key));
  return CONTENT_PIPELINE_PRESETS.every((p) =>
    Object.entries(p.columns).every(
      ([k, g]) =>
        keys.has(k) && keys.has(g.approve_to) && keys.has(g.rework_to) && keys.has(g.reject_to),
    ),
  );
}

type GateRowBody = {
  seat_id: null;
  from_status: string | null;
  to_status: string;
  mode: "ask" | "hold" | "trust";
  hold_minutes?: number;
};

/**
 * BOARD-GATES §2.5: "Review everything" · "Approve ideas, trust the rest" · "Autopilot". Picking
 * one writes the board's statuses with its person-only columns, then its board-default gates.
 */
export function GatePresetPicker({
  board,
  editable,
  save,
  putRow,
}: {
  board: Board;
  editable: boolean;
  save: ((set: Status[]) => Promise<unknown>) | undefined;
  putRow: ((body: GateRowBody) => Promise<unknown>) | undefined;
}) {
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const current = matchingGatePreset(board.status_set as never, CONTENT_PIPELINE_PRESETS);
  const pick = async (key: string) => {
    const p = CONTENT_PIPELINE_PRESETS.find((x) => x.key === key);
    if (!p || !save) return;
    setPending(true);
    setError(null);
    try {
      await save(applyGatePreset(board.status_set as never, p) as Status[]);
      for (const r of p.rows)
        await putRow?.({
          seat_id: null,
          from_status: r.from_status,
          to_status: r.to_status,
          mode: r.mode,
          ...(r.hold_minutes ? { hold_minutes: r.hold_minutes } : {}),
        });
    } catch (e) {
      setError(problemText(e, "The review setting could not be saved."));
    } finally {
      setPending(false);
    }
  };
  return (
    <div data-slot="gate-presets" className="flex flex-col gap-1">
      <div role="radiogroup" aria-label="Who reviews" className="flex flex-col gap-1">
        {CONTENT_PIPELINE_PRESETS.map((p) => (
          <label key={p.key} className="flex items-start gap-2">
            <input
              type="radio"
              name={`gate-preset-${board.id}`}
              value={p.key}
              checked={current?.key === p.key}
              disabled={!editable || pending}
              onChange={() => void pick(p.key)}
              className="mt-1"
            />
            <span className="min-w-0">
              <span className="font-medium text-foreground">{p.name}</span>
              <span className="block text-xs text-muted-foreground">{p.hint}</span>
            </span>
          </label>
        ))}
      </div>
      {current ? null : (
        <p className="text-xs text-muted-foreground">Custom: the locked columns are set by hand.</p>
      )}
      {error ? (
        <p role="alert" className="text-xs text-danger-text">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function StatusEditor({
  statuses,
  save,
}: {
  statuses: readonly Status[];
  save: (set: Status[]) => Promise<unknown>;
}) {
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [adding, setAdding] = useState("");
  const write = async (next: Status[]) => {
    setPending(true);
    setError(null);
    try {
      await save(next);
      return true;
    } catch (e) {
      setError(problemText(e, "The statuses could not be saved."));
      return false;
    } finally {
      setPending(false);
    }
  };
  const move = (i: number, d: number) => {
    const next = [...statuses];
    const [s] = next.splice(i, 1);
    if (!s) return;
    next.splice(i + d, 0, s);
    void write(next);
  };
  const rename = async (e: FormEvent, key: string) => {
    e.preventDefault();
    const name = draft.trim();
    if (!name) return;
    if (await write(statuses.map((s) => (s.key === key ? { ...s, name } : s)))) setRenaming(null);
  };
  const add = async (e: FormEvent) => {
    e.preventDefault();
    const name = adding.trim();
    if (!name) return;
    const key = statusKey(name, new Set(statuses.map((s) => s.key)));
    // A new column goes before the first done one (Work OS: open statuses, then done).
    const at = statuses.findIndex((s) => s.done);
    const next = [...statuses];
    next.splice(at < 0 ? next.length : at, 0, { key, name, done: false });
    if (await write(next)) setAdding("");
  };
  return (
    <div data-slot="status-editor" className="flex flex-col gap-1">
      <ul aria-label="Statuses" className="m-0 flex list-none flex-col p-0">
        {statuses.map((s, i) => (
          <li
            key={s.key}
            data-status={s.key}
            className="group/status flex min-h-8 items-center gap-1 rounded-md px-1 hover:bg-muted"
          >
            {renaming === s.key ? (
              <form className="flex flex-1 items-center gap-1" onSubmit={(e) => rename(e, s.key)}>
                <Input
                  aria-label="Status name"
                  autoFocus
                  className="h-7"
                  value={draft}
                  maxLength={200}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => e.key === "Escape" && setRenaming(null)}
                />
                <Button
                  type="submit"
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Save the name"
                  disabled={pending || !draft.trim()}
                >
                  <CheckIcon aria-hidden="true" />
                </Button>
              </form>
            ) : (
              <>
                <span className="min-w-0 flex-1 truncate">{s.name}</span>
                {s.gate ? <ColumnLock /> : null}
                {s.done ? <Badge variant="success">Done</Badge> : null}
                <span
                  className={cn(
                    "flex items-center opacity-0 group-hover/status:opacity-100 group-focus-within/status:opacity-100 pointer-coarse:opacity-100",
                  )}
                >
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`Rename ${s.name}`}
                    title="Rename"
                    disabled={pending}
                    onClick={() => {
                      setDraft(s.name);
                      setRenaming(s.key);
                    }}
                  >
                    <PencilIcon aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`Move ${s.name} up`}
                    title="Move up"
                    disabled={pending || i === 0}
                    onClick={() => move(i, -1)}
                  >
                    <ArrowUpIcon aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`Move ${s.name} down`}
                    title="Move down"
                    disabled={pending || i === statuses.length - 1}
                    onClick={() => move(i, 1)}
                  >
                    <ArrowDownIcon aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`Remove ${s.name}`}
                    title="Remove"
                    className="hover:text-destructive"
                    disabled={pending || statuses.length === 1}
                    onClick={() => void write(statuses.filter((x) => x.key !== s.key))}
                  >
                    <Trash2Icon aria-hidden="true" />
                  </Button>
                </span>
              </>
            )}
          </li>
        ))}
      </ul>
      <form className="flex items-center gap-1" onSubmit={add}>
        <Input
          aria-label="New status"
          placeholder="Add a status"
          className="h-8"
          maxLength={200}
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
        />
        <Button
          type="submit"
          variant="outline"
          size="sm"
          disabled={pending || !adding.trim()}
          aria-label="Add the status"
        >
          <PlusIcon aria-hidden="true" />
          Add
        </Button>
      </form>
      {error ? (
        <p role="alert" className="text-xs text-danger-text">
          {error}
        </p>
      ) : null}
    </div>
  );
}

const off = registerBoardSettingsTab({
  id: "general",
  title: "General",
  order: 0,
  Component: GeneralTab,
});
// A hot reload re-runs this module: the old registration goes first (no duplicate id).
import.meta.hot?.dispose(off);
